/**
 * AttachHgipBulkDialog — one-click admin action that copies the HGIP
 * annexure PDF into every "sales-role" letter template that doesn't
 * already have it.
 *
 * Flow:
 *   1. Open dialog → backend does a `dry_run` to show admin which
 *      templates WILL be updated (and which are already attached).
 *   2. Admin clicks "Attach" → backend re-runs with `dry_run=false`,
 *      writes copies to disk + DB.
 *   3. Show final summary with attached / already-skipped / not-sales
 *      counts.
 */
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, CheckCircle2, Zap, AlertCircle } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { attachHgipBulk } from '../api';

export default function AttachHgipBulkDialog({ open, onOpenChange }) {
  const [preview, setPreview] = useState(null);   // dry-run report
  const [result, setResult] = useState(null);     // post-action report
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);

  // When dialog opens, fetch a dry-run preview. When it closes the parent
  // re-mounts via `key`, so we don't need a manual cleanup branch here
  // (which would otherwise trip the React Compiler set-state-in-effect rule).
  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await attachHgipBulk({ dry_run: true });
        if (!cancelled) setPreview(data);
      } catch (e) {
        if (!cancelled) {
          toast.error(e?.response?.data?.detail || 'Failed to preview templates');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  const handleRun = async () => {
    setRunning(true);
    try {
      const { data } = await attachHgipBulk({ dry_run: false });
      setResult(data);
      const n = data.summary.attached_count;
      if (n > 0) toast.success(`HGIP attached to ${n} template${n === 1 ? '' : 's'}`);
      else toast.info('No templates needed updating — already attached or none matched.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Bulk attach failed');
    } finally {
      setRunning(false);
    }
  };

  const view = result || preview;

  return (
    <Dialog open={open} onOpenChange={(o) => !running && onOpenChange(o)}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Zap size={18} className="text-cyan-600" />
            Attach HGIP to Sales-Role Templates
          </DialogTitle>
          <DialogDescription className="text-sm text-slate-600 mt-1.5">
            Copies <code>static/annexures/hgip_program.pdf</code> into every
            template whose name suggests a sales role (sales, GoNukkad, DC,
            TL, field, merchant, growth…). Idempotent — already-attached
            templates are skipped automatically.
          </DialogDescription>
        </DialogHeader>

        {loading && !view && (
          <div className="flex items-center justify-center py-10 text-slate-500">
            <Loader2 size={20} className="animate-spin mr-2" /> Scanning templates…
          </div>
        )}

        {view && (
          <div className="space-y-4" data-testid="hgip-bulk-report">
            <div className="grid grid-cols-3 gap-3">
              <Stat
                label="Will attach"
                value={result ? view.summary.attached_count : view.attached.length}
                tone="ok"
                done={!!result}
              />
              <Stat label="Already attached" value={view.summary.skipped_already} tone="muted" />
              <Stat label="Not a sales template" value={view.summary.skipped_not_sales} tone="muted" />
            </div>

            {view.attached.length > 0 && (
              <Section
                title={result ? 'Attached just now' : 'Pending — will be attached on click'}
                items={view.attached.map((a) => ({
                  primary: a.name,
                  secondary: `${a.template_id} · tag: ${a.designation_tag}`,
                }))}
                accent="ok"
              />
            )}

            {view.skipped_already_attached.length > 0 && (
              <Section
                title="Already attached — skipping"
                items={view.skipped_already_attached.slice(0, 6).map((s) => ({
                  primary: s.name,
                  secondary: s.template_id,
                }))}
                accent="muted"
                more={Math.max(0, view.skipped_already_attached.length - 6)}
              />
            )}

            {view.skipped_not_sales_role.length > 0 && !result && (
              <Section
                title="Not sales-role templates — skipping"
                items={view.skipped_not_sales_role.slice(0, 4).map((s) => ({
                  primary: s.name,
                  secondary: s.template_id,
                }))}
                accent="muted"
                more={Math.max(0, view.skipped_not_sales_role.length - 4)}
              />
            )}
          </div>
        )}

        <div className="flex items-center justify-end gap-2 pt-3 border-t mt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={running} data-testid="hgip-bulk-cancel-btn">
            {result ? 'Close' : 'Cancel'}
          </Button>
          {!result && (
            <Button
              onClick={handleRun}
              disabled={running || loading || !preview || preview.attached.length === 0}
              data-testid="hgip-bulk-run-btn"
            >
              {running
                ? <><Loader2 size={14} className="mr-2 animate-spin" /> Attaching…</>
                : <>Attach to {preview?.attached?.length ?? 0} template{(preview?.attached?.length ?? 0) === 1 ? '' : 's'}</>}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}


function Stat({ label, value, tone, done }) {
  const colors = {
    ok: done ? 'bg-emerald-100 border-emerald-200 text-emerald-700' : 'bg-cyan-50 border-cyan-200 text-cyan-700',
    muted: 'bg-slate-50 border-slate-200 text-slate-600',
  };
  return (
    <div className={`rounded-lg border p-3 text-center ${colors[tone]}`}>
      <div className="text-[10px] uppercase tracking-wide opacity-80">{label}</div>
      <div className="text-2xl font-bold">{value}</div>
    </div>
  );
}


function Section({ title, items, accent, more }) {
  const Icon = accent === 'ok' ? CheckCircle2 : AlertCircle;
  const iconClass = accent === 'ok' ? 'text-emerald-600' : 'text-slate-400';
  return (
    <div className="rounded-md border border-slate-200">
      <div className="px-3 py-2 bg-slate-50 text-xs font-semibold text-slate-700 border-b">{title}</div>
      <div className="divide-y divide-slate-100">
        {items.map((it, idx) => (
          <div key={idx} className="flex items-center gap-2 px-3 py-1.5 text-sm">
            <Icon size={14} className={iconClass} />
            <div className="flex-1 min-w-0">
              <div className="truncate">{it.primary}</div>
              <div className="text-[10px] text-slate-500 font-mono truncate">{it.secondary}</div>
            </div>
          </div>
        ))}
        {more > 0 && (
          <div className="px-3 py-1.5 text-xs text-slate-500">…and {more} more</div>
        )}
      </div>
    </div>
  );
}
